import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { Modal, Spinner } from "react-bootstrap";
import dayjs from "dayjs";
import { formatDateTime } from "../../../hooks/formatDateTime";
import {
  get_homepage_hero_slides,
  save_homepage_hero_slides,
} from "../../../reduxData/cms/cmsAction";
import { createEmptySlide, makeId, MAX_SLIDES } from "./homepageHero.constants";
import { countErrors, hasErrors, validateSlide } from "./homepageHero.validation";
import {
  getSlideScheduleStatus,
  normalizeForSave,
  normalizeHomepageHeroData,
  stripHtml,
} from "./homepageHero.helpers";
import HeroSlideForm from "./HeroSlideForm";
import "./HomepageHeroSlides.css";

const reorderSlides = (slides) => {
  const regularSlides = slides.filter((slide) => !slide.isFallback);
  const fallbackSlide = slides.find((slide) => slide.isFallback);
  return [...regularSlides, ...(fallbackSlide ? [fallbackSlide] : [])].map((slide, index) => ({ ...slide, order: index + 1 }));
};
const formatDateRange = (slide) => {
  if (slide.isFallback) return "Used when no regular slide is available";
  if (slide.is_permanent) return "Permanent";
  if (!slide.startDateTime || !slide.endDateTime) return "Schedule incomplete";
  const dateOnly = { year: undefined, hour: undefined, minute: undefined };
  return `${formatDateTime(slide.startDateTime, undefined, false, dateOnly)} – ${formatDateTime(slide.endDateTime, undefined, false, dateOnly)}`;
};

function StatCard({ label, value, tone }) {
  return <div className={`hero-stat hero-stat--${tone}`}><strong>{value}</strong><span>{label}</span></div>;
}

function PageHeader({ stats, canEdit, atLimit, onAdd }) {
  return (
    <header className="hero-page-header">
      <div className="hero-page-title">
        <h1>Homepage Hero Slides</h1>
        <p>Manage responsive, scheduled homepage banners for buyers and sellers.</p>
      </div>
      <div className="hero-header-actions">
        <div className="hero-stats">
          <StatCard label="Total" value={stats.total} tone="neutral" />
          <StatCard label="Active" value={stats.active} tone="success" />
          <StatCard label="Scheduled" value={stats.scheduled} tone="warning" />
          <StatCard label="Inactive" value={stats.inactive} tone="muted" />
        </div>
        {canEdit && <button type="button" className="hero-button hero-button--primary" disabled={atLimit} onClick={onAdd}>＋ Add New Slide</button>}
      </div>
    </header>
  );
}

function StatusPill({ status }) {
  return <span className={`hero-pill hero-pill--${status.toLowerCase()}`}>{status.replaceAll("_", " ")}</span>;
}

function SlideCard({
  slide,
  index,
  expanded,
  errors,
  canEdit,
  canDelete,
  dragProvided,
  dragSnapshot,
  onToggle,
  onDuplicate,
  onDelete,
  onActiveChange,
  onUpdate,
  onUpdateImmediate,
  onCancel,
  onSave,
  isDirty,
  isSaving,
  busy,
  isFallback,
  dispatch,
  isNew,
}) {
  const status = getSlideScheduleStatus(slide);
  const title = stripHtml(slide.title) || "Untitled slide";
  const errorCount = countErrors(errors);

  return (
    <article
      id={`slide-${slide.id}`}
      ref={dragProvided.innerRef}
      className={`hero-slide-card ${dragSnapshot.isDragging ? "is-dragging" : ""} ${expanded ? "is-expanded" : ""} ${isFallback ? "is-fallback" : ""}`}
      {...dragProvided.draggableProps}
    >
      <div className="hero-slide-summary">
        <span className={`hero-drag-handle ${isFallback ? "is-disabled" : ""}`} {...(!isFallback ? dragProvided.dragHandleProps : {})} role={isFallback ? undefined : "button"} tabIndex={isFallback ? undefined : "0"} aria-label={isFallback ? undefined : `Reorder slide ${index + 1}`}><svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
          <path d="M0 0h24v24H0z" fill="none" />
          <path fill="currentColor" d="M11 11V5.828L9.172 7.657L7.757 6.243L12 2l4.243 4.243l-1.415 1.414L13 5.828V11h5.172l-1.829-1.828l1.414-1.415L22 12l-4.243 4.243l-1.414-1.415L18.172 13H13v5.172l1.828-1.829l1.415 1.414L12 22l-4.243-4.243l1.415-1.414L11 18.172V13H5.828l1.829 1.828l-1.414 1.415L2 12l4.243-4.243l1.414 1.415L5.828 11z" />
        </svg>
        </span>
        <button type="button" className="hero-slide-identity" onClick={onToggle}>
          <span>{isFallback ? "Fallback slide" : `Slide ${index + 1}`}</span>
          <strong>{title}</strong>
          <small>{slide.audienceType} · {status.replaceAll("_", " ")}</small>
        </button>
        <div className="hero-slide-completeness">
          <span>Desktop {slide.desktopImage ? "✓" : "—"}</span>
          <span>Tablet {slide.tabletImage ? "✓" : "—"}</span>
          <span>Mobile {slide.mobileImage ? "✓" : "—"}</span>
          <span>{formatDateRange(slide)}</span>
        </div>
        <div className="hero-slide-state"><StatusPill status={status} /></div>
        <div className="hero-slide-actions">
          <button type="button" className="hero-button hero-button--small hero-button--neutral" onClick={onToggle}>{expanded ? "Collapse" : "Edit"}</button>
          {/* {canEdit && <button type="button" className="hero-button hero-button--small hero-button--neutral" disabled={busy} onClick={onDuplicate}>Duplicate</button>} */}
          {canDelete && !isFallback && <label className="hero-compact-toggle"><input type="checkbox" checked={slide.isActive} disabled={busy} onChange={(event) => onActiveChange(event.target.checked)} /><span /></label>}
          {canDelete && !isFallback && <button type="button" className="hero-icon-button hero-icon-button--danger" disabled={busy} onClick={onDelete} aria-label={`Delete ${title}`}><svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
            <path d="M0 0h24v24H0z" fill="none" />
            <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4.687 6.213L6.8 18.976a2.5 2.5 0 0 0 2.466 2.092h3.348m6.698-14.855L17.2 18.976a2.5 2.5 0 0 1-2.466 2.092h-3.348m-1.364-9.952v5.049m3.956-5.049v5.049M2.75 6.213h18.5m-6.473 0v-1.78a1.5 1.5 0 0 0-1.5-1.5h-2.554a1.5 1.5 0 0 0-1.5 1.5v1.78z" />
          </svg>
          </button>}
        </div>
      </div>

      {expanded && (
        <div className="hero-slide-editor">
          {errorCount > 0 && <div className="hero-validation-summary"><strong>{errorCount} fields need attention.</strong><span>Review the highlighted fields before saving.</span></div>}
          <HeroSlideForm slide={slide} errors={errors} disabled={!canEdit} dispatch={dispatch} isNew={isNew} isFallback={isFallback} update={onUpdate} updateImmediate={onUpdateImmediate} />
          {canEdit && (
            <div className="hero-slide-editor-actions">
              <span>{isDirty ? "This slide has unsaved changes." : "This slide is up to date."}</span>
              <div>
                <button type="button" className="hero-button hero-button--neutral" disabled={isSaving} onClick={onCancel}>Cancel</button>
                <button type="button" className="hero-button hero-button--primary" disabled={isSaving} onClick={onSave}>{isSaving ? "Saving…" : "Save slide"}</button>
              </div>
            </div>
          )}
        </div>
      )}
    </article>
  );
}

export default function HomeSlides({ }) {
  const dispatch = useDispatch();
  const contentType = "home_slides_content";
  const { userPermissions } = useSelector((state) => state.auth);
  const record = useSelector((state) => contentType.endsWith("_nl") ? state.cms.homepageHeroSlidesNl : state.cms.homepageHeroSlides);
  const canEdit = userPermissions?.cms?.edit === true;
  const canDelete = userPermissions?.cms?.delete === true;

  const [slides, setSlides] = useState([]);
  const [savedSlides, setSavedSlides] = useState([]);
  const [expandedSlideId, setExpandedSlideId] = useState(null);
  const [errors, setErrors] = useState({});
  const [dirtySlideIds, setDirtySlideIds] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [savingSlideId, setSavingSlideId] = useState(null);
  const [autoSaving, setAutoSaving] = useState(false);
  const [apiError, setApiError] = useState("");
  const [legacyContent, setLegacyContent] = useState(false);
  const [migrationNoticeVisible, setMigrationNoticeVisible] = useState(true);
  const [slideToDelete, setSlideToDelete] = useState(null);
  const recordIdRef = useRef(record?.id || null);
  const skipNextHydration = useRef(false);
  const slidesRef = useRef([]);

  const loadSlides = useCallback(async () => {
    setLoading(true);
    setApiError("");
    try {
      await get_homepage_hero_slides(contentType, dispatch);
    } catch (error) {
      setApiError("Hero slides could not be loaded. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [contentType, dispatch]);

  useEffect(() => { loadSlides(); }, [loadSlides]);
  useEffect(() => { slidesRef.current = slides; }, [slides]);
  useEffect(() => {
    if (!record) return;
    recordIdRef.current = record.id || recordIdRef.current;
    if (skipNextHydration.current) {
      skipNextHydration.current = false;
      return;
    }
    if (dirtySlideIds.size) return;
    const normalized = normalizeHomepageHeroData(record);
    slidesRef.current = normalized.slides;
    setSlides(normalized.slides);
    setSavedSlides(normalized.slides);
    setLegacyContent(normalized.migrated);
    setExpandedSlideId(null);
  }, [record]);
  useEffect(() => {
    const warnBeforeLeaving = (event) => {
      if (!dirtySlideIds.size) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeLeaving);
    return () => window.removeEventListener("beforeunload", warnBeforeLeaving);
  }, [dirtySlideIds]);

  const changeSlides = (updater, dirtySlideId) => {
    const nextSlides = reorderSlides(updater(slidesRef.current));
    slidesRef.current = nextSlides;
    setSlides(nextSlides);
    if (dirtySlideId) {
      setDirtySlideIds((current) => new Set(current).add(dirtySlideId));
    }
  };

  const clearFieldError = (slideId, field, nestedGroup) => {
    setErrors((current) => {
      if (!current[slideId]) return current;
      const nextSlideErrors = { ...current[slideId] };
      if (nestedGroup) {
        const groupErrors = { ...(nextSlideErrors[nestedGroup] || {}) };
        delete groupErrors[field];
        if (Object.keys(groupErrors).length) nextSlideErrors[nestedGroup] = groupErrors;
        else delete nextSlideErrors[nestedGroup];
      } else {
        delete nextSlideErrors[field];
      }
      const next = { ...current };
      if (hasErrors(nextSlideErrors)) next[slideId] = nextSlideErrors;
      else delete next[slideId];
      return next;
    });
  };

  const updateSlide = (slideId, field, value) => {
    changeSlides((items) => items.map((slide) => slide.id === slideId ? { ...slide, [field]: value } : slide), slideId);
    clearFieldError(slideId, field);
  };

  const updateSlideImmediately = (slideId, field, value) => {
    slidesRef.current = slidesRef.current.map((slide) => slide.id === slideId ? { ...slide, [field]: value } : slide);
  };

  const scrollToSlide = (slideId) => {
    window.setTimeout(() => document.getElementById(`slide-${slideId}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
  };

  const addSlide = () => {
    const regularSlideCount = slides.filter((slide) => !slide.isFallback).length;
    if (regularSlideCount >= MAX_SLIDES) return;
    const newSlide = createEmptySlide(regularSlideCount + 1);
    changeSlides((items) => [...items.filter((slide) => !slide.isFallback), newSlide, ...items.filter((slide) => slide.isFallback)], newSlide.id);
    setExpandedSlideId(newSlide.id);
    scrollToSlide(newSlide.id);
  };

  const duplicateSlide = (slideId) => {
    const original = slides.find((slide) => slide.id === slideId);
    if (!original || original.isFallback) return;
    const duplicate = {
      ...original,
      id: makeId(),
      buttons: (original.buttons || []).map((button, index) => ({ ...button, id: makeId(), order: index + 1 })),
    };
    changeSlides((items) => [...items.filter((slide) => !slide.isFallback), duplicate, ...items.filter((slide) => slide.isFallback)], duplicate.id);
    setExpandedSlideId(duplicate.id);
    scrollToSlide(duplicate.id);
  };

  const persistSlides = async (nextSavedSlides) => {
    setApiError("");
    await save_homepage_hero_slides({
      name: contentType,
      type: contentType,
      status: true,
      content: { slides: normalizeForSave(reorderSlides(nextSavedSlides)) },
    }, recordIdRef.current, dispatch);
    if (!recordIdRef.current) {
      skipNextHydration.current = true;
      const savedRecord = await get_homepage_hero_slides(contentType, dispatch);
      recordIdRef.current = savedRecord?.id || null;
    }
    setSavedSlides(reorderSlides(nextSavedSlides));
  };

  const deleteSlide = async () => {
    if (!slideToDelete || slideToDelete.isFallback) return;
    const deletingId = slideToDelete.id;
    const wasSaved = savedSlides.some((slide) => slide.id === deletingId);
    setAutoSaving(true);
    try {
      if (wasSaved) await persistSlides(savedSlides.filter((slide) => slide.id !== deletingId));
      setSlides((items) => reorderSlides(items.filter((slide) => slide.id !== deletingId)));
      setDirtySlideIds((current) => { const next = new Set(current); next.delete(deletingId); return next; });
      setErrors((current) => { const next = { ...current }; delete next[deletingId]; return next; });
      setSlideToDelete(null);
    } catch (error) {
      setApiError("Delete failed. The slide was not removed.");
    } finally {
      setAutoSaving(false);
    }
  };

  const handleDragEnd = async ({ source, destination }) => {
    if (!destination || !canEdit || source.index === destination.index) return;
    const previousSlides = slides;
    const reordered = [...slides];
    const [movedSlide] = reordered.splice(source.index, 1);
    reordered.splice(destination.index, 0, movedSlide);
    setSlides(reorderSlides(reordered));

    const savedById = new Map(savedSlides.map((slide) => [slide.id, slide]));
    const reorderedSaved = reordered.map((slide) => savedById.get(slide.id)).filter(Boolean);
    setAutoSaving(true);
    try {
      await persistSlides(reorderedSaved);
    } catch (error) {
      setSlides(previousSlides);
      setApiError("Reordering failed. The previous order was restored.");
    } finally {
      setAutoSaving(false);
    }
  };

  const saveSlide = async (slideId) => {
    // Rich-text editor changes and a Save click can occur in the same browser
    // frame. Read the synchronously updated ref so the request never uses the
    // previous render's title/subtitle values.
    const currentSlides = slidesRef.current;
    const slide = currentSlides.find((item) => item.id === slideId);
    if (!slide) return;
    const isNew = !savedSlides.some((item) => item.id === slideId);
    const slideErrors = validateSlide(slide, { isNew });
    if (hasErrors(slideErrors)) {
      setErrors((current) => ({ ...current, [slideId]: slideErrors }));
      scrollToSlide(slideId);
      return;
    }

    const savedById = new Map(savedSlides.map((item) => [item.id, item]));
    savedById.set(slideId, slide);
    const nextSavedSlides = currentSlides.map((item) => savedById.get(item.id)).filter(Boolean);
    setSavingSlideId(slideId);
    setApiError("");
    try {
      await persistSlides(nextSavedSlides);
      setDirtySlideIds((current) => { const next = new Set(current); next.delete(slideId); return next; });
      setErrors((current) => { const next = { ...current }; delete next[slideId]; return next; });
    } catch (error) {
      setApiError("Save failed. This slide's local changes are still available.");
    } finally {
      setSavingSlideId(null);
    }
  };

  const cancelSlide = (slideId) => {
    const savedSlide = savedSlides.find((item) => item.id === slideId);
    if (savedSlide) {
      setSlides((items) => reorderSlides(items.map((item) => item.id === slideId ? savedSlide : item)));
    } else {
      setSlides((items) => reorderSlides(items.filter((item) => item.id !== slideId)));
      setExpandedSlideId(null);
    }
    setDirtySlideIds((current) => { const next = new Set(current); next.delete(slideId); return next; });
    setErrors((current) => { const next = { ...current }; delete next[slideId]; return next; });
  };

  const changeActiveState = async (slideId, value) => {
    const previousSlides = slides;
    setSlides((items) => items.map((slide) => slide.id === slideId ? { ...slide, isActive: value } : slide));
    const savedSlide = savedSlides.find((slide) => slide.id === slideId);
    if (!savedSlide) return;
    const nextSavedSlides = savedSlides.map((slide) => slide.id === slideId ? { ...slide, isActive: value } : slide);
    setAutoSaving(true);
    try {
      await persistSlides(nextSavedSlides);
    } catch (error) {
      setSlides(previousSlides);
      setApiError("Status update failed. The previous status was restored.");
    } finally {
      setAutoSaving(false);
    }
  };

  const stats = useMemo(() => ({
    total: slides.length,
    active: slides.filter((slide) => !slide.isFallback && getSlideScheduleStatus(slide) === "ACTIVE_NOW").length,
    scheduled: slides.filter((slide) => !slide.isFallback && getSlideScheduleStatus(slide) === "UPCOMING").length,
    inactive: slides.filter((slide) => !slide.isFallback && !slide.isActive).length,
  }), [slides]);

  if (loading) return <div className="hero-loading"><Spinner animation="border" /><span>Loading hero slides…</span></div>;

  return (
    <div className="home-hero-admin">
      <PageHeader stats={stats} canEdit={canEdit} atLimit={slides.filter((slide) => !slide.isFallback).length >= MAX_SLIDES} onAdd={addSlide} />

      {apiError && <div className="hero-api-error"><div><strong>Something went wrong</strong><span>{apiError}</span></div><button type="button" onClick={loadSlides}>Retry</button></div>}
      {legacyContent && migrationNoticeVisible && <div className="hero-migration-notice"><span className="hero-migration-icon">i</span><div><strong>Legacy content detected</strong><p>The previous single-slide content is displayed in the new format. It will only be migrated when you save.</p></div><button type="button" onClick={() => setMigrationNoticeVisible(false)} aria-label="Dismiss">×</button></div>}
      {!canEdit && <div className="hero-read-only">You have read-only access to this content.</div>}

      {slides.length === 0 ? (
        <div className="hero-empty"><span>▧</span><h3>No hero slides yet</h3><p>Create the first responsive homepage banner.</p>{canEdit && <button type="button" className="hero-button hero-button--primary" onClick={addSlide}>Create first slide</button>}</div>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="hero-slides" isDropDisabled={!canEdit || autoSaving}>
            {(provided) => (
              <div className="hero-slide-list" ref={provided.innerRef} {...provided.droppableProps}>
                {slides.map((slide, index) => (
                  <Draggable key={slide.id} draggableId={slide.id} index={index} isDragDisabled={!canEdit || autoSaving || slide.isFallback}>
                    {(dragProvided, snapshot) => (
                      <SlideCard
                        slide={slide}
                        index={index}
                        expanded={expandedSlideId === slide.id}
                        errors={errors[slide.id] || {}}
                        canEdit={canEdit}
                        canDelete={canDelete && slides.length > 1}
                        dragProvided={dragProvided}
                        dragSnapshot={snapshot}
                        dispatch={dispatch}
                        isNew={!savedSlides.some((savedSlide) => savedSlide.id === slide.id)}
                        isDirty={dirtySlideIds.has(slide.id)}
                        isSaving={savingSlideId === slide.id}
                        busy={autoSaving || Boolean(savingSlideId)}
                        isFallback={Boolean(slide.isFallback)}
                        onToggle={() => setExpandedSlideId(expandedSlideId === slide.id ? null : slide.id)}
                        onDuplicate={() => duplicateSlide(slide.id)}
                        onDelete={() => setSlideToDelete(slide)}
                        onActiveChange={(value) => changeActiveState(slide.id, value)}
                        onUpdate={(field, value) => updateSlide(slide.id, field, value)}
                        onUpdateImmediate={(field, value) => updateSlideImmediately(slide.id, field, value)}
                        onCancel={() => cancelSlide(slide.id)}
                        onSave={() => saveSlide(slide.id)}
                      />
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      <Modal show={Boolean(slideToDelete)} onHide={() => setSlideToDelete(null)} centered className="hero-delete-modal">
        <Modal.Header closeButton><Modal.Title>Delete slide?</Modal.Title></Modal.Header>
        <Modal.Body>Delete “{stripHtml(slideToDelete?.title) || "Untitled slide"}”? This action will be saved immediately.</Modal.Body>
        <Modal.Footer><button type="button" className="hero-button hero-button--neutral" disabled={autoSaving} onClick={() => setSlideToDelete(null)}>Cancel</button><button type="button" className="hero-button hero-button--danger" disabled={autoSaving} onClick={deleteSlide}>{autoSaving ? "Deleting…" : "Delete slide"}</button></Modal.Footer>
      </Modal>
    </div>
  );
}
